import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { confirmExtractedItinerary } from '@/redux/states/flights/externalFlightBookings/confirmExtractedItinerary/confirmExtractedItinerary.api'
import { resetConfirmState } from '@/redux/states/flights/externalFlightBookings/confirmExtractedItinerary/confirmExtractedItinerary.slice'
import { uploadPdf } from '@/redux/states/flights/externalFlightBookings/uploadPdf/uploadPdf.api'
import {
  resetUploadState,
  setUploadProgress
} from '@/redux/states/flights/externalFlightBookings/uploadPdf/uploadPdf.slice'
import { getCountriesAndCities } from '@/redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { getExternalAirlines } from '@/redux/states/flights/getExternalAirlines/getExternalAirlines.slice'
import { GetExternalAirlinesRequestPayload } from '@/redux/states/flights/getExternalAirlines/getExternalAirlines.types'
import router from '@/router/functions/router.functions'
import routes from '@/router/routes.dictionary'
import Snackbar from '@/shared/components/Snackbar/Snackbar.functions'
import { SnackbarAutoHideDuration } from '@/shared/components/Snackbar/Snackbar.types'
import logger from '@/shared/functions/Logger/logger.functions'
import { joinStringsWithBullet } from '@/shared/functions/String/string.functions'
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux.hooks'
import { PictureAsPdf } from '@mui/icons-material'
import { Alert, Box, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useDropzone } from 'react-dropzone'

import Modal from '../Modal/Modal.component'
import ExternalBookingsConfirmationModal from './components/ExternalBookingsConfirmationModal.component'

import ModalFunctions from '../Modal/Modal.functions'
import styles from './UploadFlightItineraryPDFModal.styles'
import { UploadFlightItineraryPDFModalProps, UploadStep } from './UploadFlightItineraryPDFModal.types'
import {
  CreateItineraryFormValues,
  ExternalBookingsConfirmationModalProps
} from './components/ExternalBookingsConfirmationModal.types'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const EMPTY_UPLOAD_PDF_STATE = {
  loading: false,
  uploadProgress: 0,
  isProcessing: false,
  error: null as string | null,
  extractionStatus: null as string | null,
  extractionAttemptId: null as string | null,
  warnings: [] as string[],
  extractedData: null as unknown | null,
  estimatedReviewTime: null as string | null,
  requestId: null as string | null,
  brand: undefined as string | undefined
}

const EMPTY_CONFIRM_EXTRACTED_ITINERARY_STATE = {
  loading: false,
  error: null as string | null,
  success: null as unknown | null
}

const UploadFlightItineraryPDFModal: React.FC<UploadFlightItineraryPDFModalProps> = (props) => {
  const { open, onClose, onSubmit, offerRequest, fulfillmentOrder } = props

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = router.navigate()

  const [step, setStep] = useState<UploadStep>('upload')
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  // Track if THIS instance initiated the current upload
  // This prevents multiple component instances from all reacting to shared Redux state
  const isUploadInitiator = useRef(false)
  const handledConfirmSuccessRef = useRef(false)

  // Track immediate upload initiation to show progress before Redux state updates
  // This bridges the gap between file selection and Redux pending state
  const [isPreparingUpload, setIsPreparingUpload] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)

  const [allAirports, setAllAirports] = useState<{ value: string; label: string }[]>([])
  const [allAirlines, setAllAirlines] = useState<{ value: string; label: string; iataCode?: string }[]>([])
  const dispatch = useAppDispatch()

  const { success: airlinesSuccess } = useAppSelector(
    (s: { getExternalAirlines?: { success?: { data?: unknown } | null } }) => s.getExternalAirlines || {}
  )
  const { success: countriesAndCitiesSuccess } = useAppSelector(
    (s: { getCountriesAndCities?: { success?: { data?: unknown } | null } }) => s.getCountriesAndCities || {}
  )
  const uploadPdfState = useAppSelector(
    (s: {
      uploadPdf?: {
        loading: boolean
        uploadProgress: number
        isProcessing: boolean
        error: string | null
        extractionStatus: string | null
        extractionAttemptId: string | null
        warnings: string[]
        extractedData: unknown | null
        estimatedReviewTime: string | null
        requestId: string | null
        brand?: string
      }
    }) => s.uploadPdf || EMPTY_UPLOAD_PDF_STATE
  )

  const {
    loading: uploading,
    uploadProgress,
    isProcessing,
    extractionStatus,
    extractionAttemptId,
    warnings,
    extractedData,
    estimatedReviewTime,
    error: uploadError
  } = uploadPdfState

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Please upload a PDF file.'
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'File size must be less than 10MB.'
    }

    return null
  }

  const doUpload = useCallback(
    (file: File) => {
      // Mark this instance as the one that initiated the upload
      isUploadInitiator.current = true

      // Show immediate feedback before Redux state updates
      setIsPreparingUpload(true)

      dispatch(
        uploadPdf({
          pdf: file,
          offerRequestId: offerRequest?.offerRequestId,
          onUploadProgress: (percentComplete: number) => dispatch(setUploadProgress(percentComplete))
        })
      )
    },
    [dispatch, offerRequest]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: Array<{ errors?: ReadonlyArray<{ code: string; message: string }> }>) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0]
        if (rejection.errors) {
          const rejectionError = rejection.errors[0]
          let errorMessage = 'File rejected. Please try again.'

          if (rejectionError.code === 'file-invalid-type') {
            errorMessage = 'Please upload a PDF file.'
          } else if (rejectionError.code === 'file-too-large') {
            errorMessage = 'File size must be less than 10MB.'
          } else if (rejectionError.message) {
            errorMessage = rejectionError.message
          }

          Snackbar.show({
            message: errorMessage,
            severity: 'error',
            autoHideDuration: SnackbarAutoHideDuration.MEDIUM
          })
        }

        return
      }

      // Handle accepted file
      const file = acceptedFiles[0]
      if (!file) return

      const validationError = validateFile(file)
      if (validationError) {
        Snackbar.show({
          message: validationError,
          severity: 'error',
          autoHideDuration: SnackbarAutoHideDuration.MEDIUM
        })

        return
      }

      setUploadedFileName(file.name)
      doUpload(file)
    },
    [doUpload]
  )

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: false,
    noClick: false,
    noKeyboard: false
  })

  // Clear preparing state once Redux upload state takes over
  useEffect(() => {
    if (uploading || isProcessing || extractionStatus) {
      setIsPreparingUpload(false)
    }
  }, [uploading, isProcessing, extractionStatus])

  useEffect(() => {
    if (uploadError) {
      setUploadedFileName(null)
    }
  }, [uploadError])

  // Fetch airports and airlines when needed
  useEffect(() => {
    if (open && extractionStatus === 'ready_for_confirmation') {
      const getExternalAirlinesPayload: GetExternalAirlinesRequestPayload = {
        onSuccess: () => {
          // Airlines fetched successfully
        }
      }
      dispatch(getExternalAirlines(getExternalAirlinesPayload))
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, open, extractionStatus])

  useEffect(() => {
    if (countriesAndCitiesSuccess?.data) {
      interface Airport {
        iataCode: string
        airportName: string
      }
      interface City {
        cityName: string
        airports: Airport[]
      }
      interface Country {
        countryName: string
        cities: City[]
      }
      const countries = countriesAndCitiesSuccess.data as Country[]
      const airports = countries
        .flatMap((country) =>
          country.cities.flatMap((city) =>
            city.airports.map((airport) => ({
              value: airport.iataCode,
              label: joinStringsWithBullet([
                `${airport.iataCode} (${airport.airportName})`,
                `${city.cityName}, ${country.countryName}`
              ])
            }))
          )
        )
        .sort((a, b) => a.value.localeCompare(b.value))
      setAllAirports(airports)
    }
  }, [countriesAndCitiesSuccess])

  useEffect(() => {
    if (airlinesSuccess?.data) {
      interface Airline {
        id: string
        name: string
        iataCode?: string
      }
      const airlinesData = airlinesSuccess.data as Airline[]
      const airlines = airlinesData
        .map((airline) => ({
          value: airline.id,
          label: airline.name,
          iataCode: airline.iataCode
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setAllAirlines(airlines)
    }
  }, [airlinesSuccess])

  useEffect(() => {
    // Only respond if this instance initiated the upload
    // This prevents multiple component instances from all reacting to shared Redux state
    if (!isUploadInitiator.current || handledConfirmSuccessRef.current) {
      return
    }

    if (
      extractionStatus === 'ready_for_confirmation' &&
      extractedData &&
      allAirports.length > 0 &&
      allAirlines.length > 0 &&
      !showConfirmationModal
    ) {
      setShowConfirmationModal(true)
      // Don't call onClose() here - we need to keep the component mounted
      // so the confirmation modal can be displayed. The upload modal will be
      // hidden via uploadModalOpen = open && !showConfirmationModal
      // Don't set step to 'review' - go directly to confirmation modal
    } else if (extractionStatus === 'under_review') {
      setStep('under_review')
    }
  }, [
    extractionStatus,
    extractedData,
    extractionAttemptId,
    allAirports.length,
    allAirlines.length,
    showConfirmationModal
  ])

  const confirmExtractedItineraryState = useAppSelector(
    (s: {
      confirmExtractedItinerary?: {
        loading: boolean
        error: string | null
        success: unknown | null
      }
    }) => s.confirmExtractedItinerary || EMPTY_CONFIRM_EXTRACTED_ITINERARY_STATE
  )

  const onSubmitRef = useRef(onSubmit)
  const onCloseRef = useRef(onClose)
  const wasOpenRef = useRef(open)

  useEffect(() => {
    onSubmitRef.current = onSubmit
    onCloseRef.current = onClose
  }, [onSubmit, onClose])

  // Reset local upload state only when THIS instance actually closes.
  // Closed OfferItem copies on the Offers page must not dispatch resets on mount —
  // that fights the instance that just created a trip and retriggers renders.
  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open

    if (!wasOpen || open || showConfirmationModal || confirmExtractedItineraryState.success) {
      return
    }

    dispatch(resetUploadState())
    dispatch(resetConfirmState())
    setStep('upload')
    setShowConfirmationModal(false)
    setIsPreparingUpload(false)
    setUploadedFileName(null)
    isUploadInitiator.current = false
    handledConfirmSuccessRef.current = false
  }, [open, dispatch, confirmExtractedItineraryState.success, showConfirmationModal])

  // Detect user's operating system
  const detectOS = (): 'mac' | 'windows' | 'ios' | 'android' | 'other' => {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const platform = window.navigator.platform.toLowerCase()

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios'
    }

    if (/android/.test(userAgent)) {
      return 'android'
    }

    if (/mac/.test(platform)) {
      return 'mac'
    }

    if (/win/.test(platform)) {
      return 'windows'
    }

    return 'other'
  }

  const renderEmailToPdfInstructions = () => {
    const os = detectOS()

    const instructions = {
      mac: [
        { app: 'Mail', steps: 'File → Export as PDF' },
        { app: 'Gmail', steps: 'Click Print → Save as PDF' },
        { app: 'Outlook', steps: 'File → Save As → PDF' }
      ],
      windows: [
        { app: 'Outlook', steps: 'File → Save As → PDF' },
        { app: 'Gmail', steps: 'Click Print → Save as PDF' },
        { app: 'Windows Mail', steps: 'Click Print → Save as PDF' }
      ],
      ios: [
        { app: 'Mail', steps: 'Tap Print → Pinch to zoom preview → Share → Save to Files' },
        { app: 'Gmail', steps: 'Tap Print → Save as PDF' }
      ],
      android: [
        { app: 'Gmail', steps: 'Tap Print → Save as PDF' },
        { app: 'Email Apps', steps: 'Tap Print → Save as PDF' }
      ],
      other: [
        { app: 'Mail', steps: 'File → Export as PDF' },
        { app: 'Gmail', steps: 'Click Print → Save as PDF' },
        { app: 'Outlook', steps: 'File → Save As → PDF' },
        { app: 'iPhone Mail', steps: 'Tap Print → Pinch to zoom → Share → Save to Files' },
        { app: 'Android', steps: 'Tap Print → Save as PDF' }
      ]
    }

    const currentInstructions = instructions[os]

    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Need to convert your email confirmation to PDF?
        </Typography>
        {currentInstructions.map((instruction, index) => (
          <Typography
            key={index}
            variant="caption"
            component="div"
            sx={{ mb: index === currentInstructions.length - 1 ? 0 : 0.5 }}
          >
            <strong>{instruction.app}:</strong> {instruction.steps}
          </Typography>
        ))}
      </Alert>
    )
  }

  const isFileBeingProcessed = Boolean(uploadedFileName && (isPreparingUpload || uploading || isProcessing))

  const uploadBody = (
    <Box sx={styles.uploadContainer}>
      {renderEmailToPdfInstructions()}

      {isFileBeingProcessed ? (
        <Box sx={styles.dropZone(false)}>
          <PictureAsPdf sx={styles.uploadedFileIcon} />

          <Typography variant="body1" sx={styles.uploadedFileName}>
            {uploadedFileName}
          </Typography>
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          sx={{
            ...styles.dropZone(true),
            ...(isDragActive && {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.light + '10'
            }),
            ...(isDragAccept && {
              borderColor: theme.palette.success.main,
              backgroundColor: theme.palette.success.light + '10'
            }),
            ...(isDragReject && {
              borderColor: theme.palette.error.main,
              backgroundColor: theme.palette.error.light + '10'
            })
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="h6" sx={{ textAlign: 'center', marginBottom: 0.5 }}>
            {isMobile
              ? 'Tap to upload your PDF'
              : isDragActive
                ? 'Drop your PDF here...'
                : 'Drag and drop your PDF anywhere to import'}
          </Typography>
          {!isMobile && !isDragActive && (
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Or click to upload
            </Typography>
          )}
        </Box>
      )}

      {isPreparingUpload && !uploading && !isProcessing && (
        <Box sx={styles.processingContainer}>
          <LinearProgress />

          <Typography variant="caption" sx={styles.processingText}>
            Preparing upload...
          </Typography>
        </Box>
      )}

      {uploading && !isProcessing && (
        <Box sx={styles.processingContainer}>
          <LinearProgress variant="determinate" value={uploadProgress} />

          <Typography variant="caption" sx={styles.processingText}>
            {uploadProgress}% uploaded
          </Typography>
        </Box>
      )}

      {isProcessing && (
        <Box sx={styles.processingContainer}>
          <LinearProgress />

          <Typography variant="caption" sx={styles.processingText}>
            Extracting flight details... This may take up to a minute.
          </Typography>
        </Box>
      )}

      {extractionStatus === 'ready_for_confirmation' &&
        !isProcessing &&
        !showConfirmationModal &&
        (allAirports.length === 0 || allAirlines.length === 0) && (
          <Box sx={styles.processingContainer}>
            <LinearProgress />

            <Typography variant="caption" sx={styles.processingText}>
              Loading flight details...
            </Typography>
          </Box>
        )}
    </Box>
  )

  const body = <Box sx={styles.modalBody}>{step === 'upload' && uploadBody}</Box>

  const getModalTitle = () => {
    switch (step) {
      case 'upload':
        return 'Upload Your Flight Itinerary'

      case 'under_review':
        return 'Your Itinerary is Under Review'

      default:
        return 'Upload Your Flight Itinerary'
    }
  }

  const getModalSubtitle = () => {
    if (step === 'upload') {
      return 'Upload your flight confirmation PDF from your airline or booking agency'
    }

    if (step === 'under_review') {
      return `We'll email you within ${estimatedReviewTime || '24-48 hours'} when your trip is ready. This extra step helps us ensure all your flight details are correct and your trip is set up perfectly for receiving offers.`
    }

    return undefined
  }

  const getHeaderRightSection = () => {
    // Confidence badge is shown in ExternalBookingsConfirmationModal instead
    return undefined
  }

  const handleGoToMyTrips = () => {
    // Reset all state when navigating
    dispatch(resetUploadState())
    dispatch(resetConfirmState())
    setStep('upload')
    setShowConfirmationModal(false)
    setUploadedFileName(null)

    if (onClose) {
      onClose()
    } else {
      ModalFunctions.close()
    }

    // Navigate to My Trips page
    navigate(routes.myTrips.path)
    window.scrollTo(0, 0)
  }

  const getPrimaryButton = () => {
    if (step === 'under_review') {
      return {
        label: 'Go to My Trips',
        onClick: handleGoToMyTrips
      }
    }

    return undefined
  }

  // Convert cabin class from form format (snake_case) to API format (camelCase)
  const convertCabinClassToApiFormat = (
    formCabinClass: string
  ): 'economy' | 'premiumEconomy' | 'business' | 'first' => {
    // Map from form format to API format
    const cabinClassMap: Record<string, 'economy' | 'premiumEconomy' | 'business' | 'first'> = {
      economy: 'economy',
      premium_economy: 'premiumEconomy',
      business: 'business',
      first: 'first'
    }

    return cabinClassMap[formCabinClass] || 'economy'
  }

  // Handle success only on the instance that uploaded the PDF. Offers mounts one
  // modal per card; they all share Redux, and unstable onSubmit/onClose identities
  // used to retrigger this effect until React hit "Maximum update depth exceeded".
  useEffect(() => {
    if (!confirmExtractedItineraryState.success) {
      handledConfirmSuccessRef.current = false

      return
    }

    if (!isUploadInitiator.current || handledConfirmSuccessRef.current) {
      return
    }

    handledConfirmSuccessRef.current = true
    setShowConfirmationModal(false)

    if (onSubmitRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSubmitRef.current(confirmExtractedItineraryState.success as any)
    } else if (onCloseRef.current) {
      onCloseRef.current()
    } else {
      ModalFunctions.close()
    }

    const resetTimeout = setTimeout(() => {
      dispatch(resetUploadState())
      dispatch(resetConfirmState())
      setStep('upload')
      setIsPreparingUpload(false)
      setUploadedFileName(null)
      isUploadInitiator.current = false
    }, 300)

    Snackbar.show({
      message: 'Trip created successfully! Go to the Requests page to see your trip.',
      action: {
        label: 'Go to Requests',
        onClick: () => {
          navigate(routes.requests.path)
          window.scrollTo(0, 0)
        }
      },
      severity: 'success',
      autoHideDuration: SnackbarAutoHideDuration.LONG
    })

    return () => {
      clearTimeout(resetTimeout)
    }
  }, [confirmExtractedItineraryState.success, navigate, dispatch])

  useEffect(() => {
    if (!confirmExtractedItineraryState.error || !isUploadInitiator.current) {
      return
    }

    Snackbar.show({
      message: confirmExtractedItineraryState.error,
      severity: 'error',
      autoHideDuration: SnackbarAutoHideDuration.MEDIUM
    })
  }, [confirmExtractedItineraryState.error])

  const handleConfirmationSubmit = useMemo(() => {
    return (formValues: CreateItineraryFormValues) => {
      if (!extractionAttemptId) {
        logger.logError(
          new Error('Missing extractionAttemptId'),
          'Cannot confirm itinerary without extractionAttemptId',
          'handleConfirmationSubmit'
        )
        Snackbar.show({
          message: 'Missing extraction data. Please try uploading the PDF again.',
          severity: 'error',
          autoHideDuration: SnackbarAutoHideDuration.LONG
        })

        return
      }

      // Map form values to API payload format
      const payload = {
        tripType: formValues.tripType,
        cabinClass: convertCabinClassToApiFormat(formValues.cabinClass),
        tripPrice: parseFloat(formValues.tripPrice),
        tripCurrency: formValues.tripCurrency,
        slices: formValues.slices.map((slice) => ({
          originAirportIataCode: slice.originAirportIataCode,
          destinationAirportIataCode: slice.destinationAirportIataCode,
          travelDate: slice.travelDate,
          segments: slice.segments.map((segment) => ({
            flightNumber: segment.flightNumber,
            airlineId: segment.airlineId, // Keep as string
            departureTime: segment.departureTime,
            arrivalTime: segment.arrivalTime,
            originAirportIataCode: segment.originAirportIataCode,
            destinationAirportIataCode: segment.destinationAirportIataCode
          }))
        })),
        offerRequestId: offerRequest?.offerRequestId,
        fulfillmentOrderId: fulfillmentOrder?.orderId,
        extractionAttemptId: extractionAttemptId
      }

      dispatch(confirmExtractedItinerary(payload))
    }
  }, [dispatch, offerRequest, fulfillmentOrder, extractionAttemptId])

  const handleCancel = () => {
    // Reset all state when canceling
    dispatch(resetUploadState())
    dispatch(resetConfirmState())
    setStep('upload')
    setShowConfirmationModal(false)
    setIsPreparingUpload(false)
    setUploadedFileName(null)
    isUploadInitiator.current = false

    if (onClose) {
      onClose()
    } else {
      ModalFunctions.close()
    }
  }

  const uploadModalOpen = open && !showConfirmationModal

  return (
    <>
      <Modal
        open={uploadModalOpen}
        onClose={handleCancel}
        title={getModalTitle()}
        subtitle={getModalSubtitle()}
        headerRightSection={getHeaderRightSection()}
        body={body}
        primaryButton={getPrimaryButton()}
        secondaryButton={{
          label: 'Cancel',
          onClick: handleCancel
        }}
      />

      <ExternalBookingsConfirmationModal
        open={showConfirmationModal}
        onClose={() => {
          // Close both modals and reset state when confirmation modal is dismissed
          setShowConfirmationModal(false)
          dispatch(resetUploadState())
          dispatch(resetConfirmState())
          setStep('upload')
          setUploadedFileName(null)
          isUploadInitiator.current = false
          // Close the parent upload modal
          if (onClose) {
            onClose()
          } else {
            ModalFunctions.close()
          }
        }}
        extractedData={extractedData as ExternalBookingsConfirmationModalProps['extractedData']}
        onSubmit={handleConfirmationSubmit}
        airports={allAirports}
        airlines={allAirlines}
        createItineraryLoading={confirmExtractedItineraryState.loading}
        warnings={warnings}
        brand={uploadPdfState.brand}
      />
    </>
  )
}

export default UploadFlightItineraryPDFModal
